// ============================================================================
// STAKEHOLDER TIME TRACKING TAB
// Consolidated time entries from all projects, jobs & appointments for a contact
// Per-person, per-job, per-project, per-activity breakdowns with date range
// Date: June 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Clock, Users, Briefcase, Car, MapPin, TrendingUp, FolderOpen,
  ChevronDown, ChevronRight, RefreshCw, Calendar,
  BarChart3,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import AppointmentReferenceBadge from '@/components/work-items/AppointmentReferenceBadge';
import type { WorkItem } from '../UniversalWorkItems';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StakeholderTimeTrackingTabProps {
  workItem: WorkItem;
  isDark: boolean;
  onRefresh?: () => void;
}

interface TimeEntry {
  id: string;
  work_item_id: string;
  user_id: number;
  user_name: string;
  started_at: string;
  ended_at: string;
  hours: number;
  duration_hours: number;
  duration_minutes: number;
  description: string;
  is_billable: boolean;
  activity_type: string;
  tracked_via: string;
  hourly_rate: number;
  total_cost: number;
  source_item_id: string;
  source_item_number: string;
  source_item_type: string;
  source_item_title: string;
  job_id: string;
  job_number: string;
  job_title: string;
  project_id: string;
  project_number: string;
  project_title: string;
  travel_km: number | null;
}

interface PersonAgg {
  user_id: number;
  user_name: string;
  total_hours: number;
  billable_hours: number;
  travel_hours: number;
  onsite_hours: number;
  travel_distance_km: number;
  entries_count: number;
  jobs_count: number;
}

interface JobAgg {
  job_id: string;
  job_number: string;
  job_title: string;
  job_status: string;
  total_hours: number;
  billable_hours: number;
  travel_hours: number;
  onsite_hours: number;
  travel_distance_km: number;
  entries_count: number;
}

interface ProjectAgg {
  project_id: string;
  project_number: string;
  project_title: string;
  project_status: string;
  total_hours: number;
  billable_hours: number;
  travel_hours: number;
  onsite_hours: number;
  travel_distance_km: number;
  entries_count: number;
  jobs_count: number;
}

interface ActivityAgg {
  activity_type: string;
  total_hours: number;
  entries_count: number;
}

interface TimeSummary {
  total_hours: number;
  billable_hours: number;
  non_billable_hours: number;
  travel_hours: number;
  onsite_hours: number;
  travel_distance_km: number;
  entry_count: number;
  projects_count: number;
  jobs_count: number;
  people_count: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const VIEWS = ['overview', 'by_project', 'by_person', 'by_job', 'entries'] as const;
type ViewId = typeof VIEWS[number];

const viewMeta: Record<ViewId, { label: string; icon: React.ElementType }> = {
  overview: { label: 'Overview', icon: BarChart3 },
  by_project: { label: 'By Project', icon: FolderOpen },
  by_person: { label: 'By Person', icon: Users },
  by_job: { label: 'By Job', icon: Briefcase },
  entries: { label: 'All Entries', icon: Clock },
};

const PERIOD_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
  { label: 'Last 6 months', days: 180 },
  { label: 'Last 12 months', days: 365 },
  { label: 'All time', days: 0 },
] as const;

const fmt = (n: number) => n.toFixed(1);
const fmtKm = (n: number) => `${n.toFixed(1)} km`;
const activityLabel = (t: string) => t?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Other';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function StakeholderTimeTrackingTab({ workItem, isDark, onRefresh: _onRefresh }: StakeholderTimeTrackingTabProps) {
  // Extract contact ID from stakeholder entity
  const contactId = useMemo(() => {
    const cf = typeof workItem.custom_fields === 'string'
      ? (() => { try { return JSON.parse(workItem.custom_fields); } catch { return {}; } })()
      : (workItem.custom_fields || {});
    return cf.entity_id || (workItem as any).contact_id || null;
  }, [workItem]);

  // State
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<ViewId>('overview');
  const [summary, setSummary] = useState<TimeSummary | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [byPerson, setByPerson] = useState<PersonAgg[]>([]);
  const [byJob, setByJob] = useState<JobAgg[]>([]);
  const [byProject, setByProject] = useState<ProjectAgg[]>([]);
  const [byActivity, setByActivity] = useState<ActivityAgg[]>([]);
  const [expandedPersons, setExpandedPersons] = useState<Set<number>>(new Set());
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  // Period state
  const [selectedPeriod, setSelectedPeriod] = useState(30);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [useCustomRange, setUseCustomRange] = useState(false);

  // Helpers
  const getHeaders = useCallback(() => {
    const tokens = authService.getTokens();
    return {
      'Content-Type': 'application/json',
      ...(tokens?.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {}),
    };
  }, []);

  // Compute date range
  const dateRange = useMemo(() => {
    if (useCustomRange && customFrom) {
      return { from: customFrom, to: customTo || undefined };
    }
    if (selectedPeriod === 0) return {};
    const from = new Date();
    from.setDate(from.getDate() - selectedPeriod);
    return { from: from.toISOString().split('T')[0] };
  }, [useCustomRange, customFrom, customTo, selectedPeriod]);

  // Fetch
  const fetchTimeData = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.set('from', dateRange.from);
      if (dateRange.to) params.set('to', dateRange.to);
      const qs = params.toString();
      const url = `/api/v1/contacts/${contactId}/time-entries${qs ? `?${qs}` : ''}`;
      const res = await fetch(url, { headers: getHeaders() });
      const j = await res.json();
      if (j.success) {
        setSummary(j.data.summary);
        setEntries(j.data.entries || []);
        setByPerson(j.data.by_person || []);
        setByJob(j.data.by_job || []);
        setByProject(j.data.by_project || []);
        setByActivity(j.data.by_activity || []);
      }
    } catch (err) {
      console.error('Failed to fetch contact time entries', err);
    } finally {
      setLoading(false);
    }
  }, [contactId, dateRange, getHeaders]);

  useEffect(() => { fetchTimeData(); }, [fetchTimeData]);

  const togglePerson = (id: number) => setExpandedPersons(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleJob = (id: string) => setExpandedJobs(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleProject = (id: string) => setExpandedProjects(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  // No contact fallback
  if (!contactId) {
    return (
      <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="font-medium">No contact linked</p>
        <p className="text-sm mt-1">Cannot load time tracking without a contact reference.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header with period selector + refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className={`text-base font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
            Customer Time Tracking
          </h3>
          <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            All time entries across projects, jobs &amp; appointments
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Quick period buttons */}
          {!useCustomRange && (
            <div className={`flex rounded-lg border overflow-hidden ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.days}
                  onClick={() => { setSelectedPeriod(opt.days); setUseCustomRange(false); }}
                  className={`px-2 py-1 text-xs font-medium transition ${
                    selectedPeriod === opt.days && !useCustomRange
                      ? isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white'
                      : isDark ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}

          {/* Custom range toggle */}
          <button
            onClick={() => setUseCustomRange(!useCustomRange)}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition ${
              useCustomRange
                ? isDark ? 'bg-blue-600 text-white' : 'bg-blue-600 text-white'
                : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <Calendar className="w-3 h-3" />
            Custom
          </button>

          {useCustomRange && (
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className={`px-2 py-1 rounded text-xs border ${
                  isDark ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                }`}
              />
              <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>to</span>
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                className={`px-2 py-1 rounded text-xs border ${
                  isDark ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'
                }`}
              />
            </div>
          )}

          <button
            onClick={fetchTimeData}
            disabled={loading}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              isDark
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 disabled:text-gray-500'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:text-gray-400'
            }`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Total Hours', value: `${fmt(summary.total_hours)}h`, icon: Clock, color: 'blue' },
            { label: 'Billable', value: `${fmt(summary.billable_hours)}h`, icon: TrendingUp, color: 'green',
              sub: summary.total_hours > 0 ? `${(summary.billable_hours / summary.total_hours * 100).toFixed(0)}%` : '0%' },
            { label: 'Travel', value: `${fmt(summary.travel_hours)}h`, icon: Car, color: 'amber',
              sub: summary.travel_distance_km > 0 ? fmtKm(summary.travel_distance_km) : undefined },
            { label: 'On-site', value: `${fmt(summary.onsite_hours)}h`, icon: MapPin, color: 'purple' },
            { label: 'Projects', value: `${summary.projects_count}`, icon: FolderOpen, color: 'indigo',
              sub: `${summary.jobs_count} jobs` },
            { label: 'People', value: `${summary.people_count}`, icon: Users, color: 'cyan',
              sub: `${summary.entry_count} entries` },
          ].map(card => {
            const Icon = card.icon;
            return (
              <div
                key={card.label}
                className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{card.label}</span>
                  <Icon className={`w-4 h-4 text-${card.color}-500`} />
                </div>
                <div className={`text-lg font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{card.value}</div>
                {card.sub && (
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{card.sub}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* View Tabs */}
      <div className={`flex border-b overflow-x-auto ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        {VIEWS.map(v => {
          const Meta = viewMeta[v];
          const Icon = Meta.icon;
          const isActive = activeView === v;
          return (
            <button
              key={v}
              onClick={() => setActiveView(v)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                isActive
                  ? isDark ? 'border-blue-500 text-blue-400' : 'border-blue-600 text-blue-600'
                  : isDark ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {Meta.label}
            </button>
          );
        })}
      </div>

      {/* Loading state */}
      {loading && (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Loading time entries…
        </div>
      )}

      {/* Empty state */}
      {!loading && summary && summary.entry_count === 0 && (
        <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No time entries found</p>
          <p className="text-sm mt-1">No tracked time for this customer in the selected period.</p>
        </div>
      )}

      {/* ======================== OVERVIEW ======================== */}
      {!loading && activeView === 'overview' && byActivity.length > 0 && (
        <div className="space-y-4">
          {/* Activity breakdown */}
          <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Hours by Activity</h4>
            </div>
            <div className="p-4 space-y-3">
              {byActivity.map(act => {
                const pct = summary && summary.total_hours > 0
                  ? (act.total_hours / summary.total_hours * 100)
                  : 0;
                return (
                  <div key={act.activity_type} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {activityLabel(act.activity_type)}
                      </span>
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {fmt(act.total_hours)}h ({pct.toFixed(0)}%)
                      </span>
                    </div>
                    <div className={`h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="h-2 rounded-full bg-blue-500 transition-all"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top contributors quick list */}
          {byPerson.length > 0 && (
            <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Top Contributors</h4>
              </div>
              <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {byPerson.slice(0, 5).map(p => (
                  <div key={p.user_id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{p.user_name}</span>
                      <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.jobs_count} job{p.jobs_count !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(p.total_hours)}h</span>
                      {p.travel_distance_km > 0 && (
                        <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{fmtKm(p.travel_distance_km)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Projects quick list */}
          {byProject.length > 0 && (
            <div className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className={`px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>Projects</h4>
              </div>
              <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {byProject.map(proj => (
                  <div key={proj.project_id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <span className={`text-sm font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{proj.project_number}</span>
                      <span className={`text-sm font-medium ml-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{proj.project_title}</span>
                      <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{proj.jobs_count} job{proj.jobs_count !== 1 ? 's' : ''}</span>
                    </div>
                    <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(proj.total_hours)}h</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ======================== BY PROJECT ======================== */}
      {!loading && activeView === 'by_project' && (
        <div className="space-y-2">
          {byProject.map(proj => {
            const isExpanded = expandedProjects.has(proj.project_id);
            const projectJobs = byJob.filter(j => {
              // Find jobs whose entries all belong to this project
              const jobEntries = entries.filter(e => e.job_id === j.job_id);
              return jobEntries.some(e => e.project_id === proj.project_id);
            });
            return (
              <div key={proj.project_id} className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button
                  onClick={() => toggleProject(proj.project_id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <div>
                      <span className={`text-sm font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{proj.project_number}</span>
                      <span className={`text-sm font-semibold ml-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{proj.project_title}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {proj.jobs_count} job{proj.jobs_count !== 1 ? 's' : ''} · {proj.entries_count} entries
                    </span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(proj.total_hours)}h</span>
                  </div>
                </button>
                {isExpanded && (
                  <div className={`border-t px-4 py-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Billable</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>{fmt(proj.billable_hours)}h</div>
                      </div>
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Travel</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{fmt(proj.travel_hours)}h</div>
                      </div>
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>On-site</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{fmt(proj.onsite_hours)}h</div>
                      </div>
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Distance</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{proj.travel_distance_km > 0 ? fmtKm(proj.travel_distance_km) : '—'}</div>
                      </div>
                    </div>
                    {/* Jobs within project */}
                    {projectJobs.length > 0 && (
                      <div className={`rounded border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className={isDark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                              <th className={`px-3 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Job</th>
                              <th className={`px-3 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                              <th className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hours</th>
                              <th className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Billable</th>
                              <th className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Entries</th>
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                            {projectJobs.map(j => (
                              <tr key={j.job_id} className={isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}>
                                <td className={`px-3 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  <span className="font-mono text-xs">{j.job_number}</span>
                                  <span className="ml-1.5">{j.job_title}</span>
                                </td>
                                <td className="px-3 py-1.5">
                                  <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                                    j.job_status === 'completed' ? (isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700')
                                    : j.job_status === 'in_progress' ? (isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700')
                                    : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                                  }`}>{j.job_status?.replace(/_/g, ' ')}</span>
                                </td>
                                <td className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{fmt(j.total_hours)}h</td>
                                <td className={`px-3 py-1.5 text-right ${isDark ? 'text-green-400' : 'text-green-600'}`}>{fmt(j.billable_hours)}h</td>
                                <td className={`px-3 py-1.5 text-right ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{j.entries_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {byProject.length === 0 && !loading && (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No project data available.</p>
          )}
        </div>
      )}

      {/* ======================== BY PERSON ======================== */}
      {!loading && activeView === 'by_person' && (
        <div className="space-y-2">
          {byPerson.map(p => {
            const isExpanded = expandedPersons.has(p.user_id);
            const personEntries = entries.filter(e => e.user_id === p.user_id);
            const billableRatio = p.total_hours > 0 ? (p.billable_hours / p.total_hours * 100) : 0;
            return (
              <div key={p.user_id} className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button
                  onClick={() => togglePerson(p.user_id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <div>
                      <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{p.user_name}</span>
                      <span className={`text-xs ml-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {p.entries_count} entries · {p.jobs_count} job{p.jobs_count !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className={`font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(p.total_hours)}h</span>
                    <span className={`${isDark ? 'text-green-400' : 'text-green-600'}`}>{billableRatio.toFixed(0)}% billable</span>
                    {p.travel_distance_km > 0 && (
                      <span className={`${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{fmtKm(p.travel_distance_km)}</span>
                    )}
                  </div>
                </button>
                {isExpanded && personEntries.length > 0 && (
                  <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={isDark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                            <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Project</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Job</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Activity</th>
                            <th className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hours</th>
                            <th className={`px-4 py-2 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Description</th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                          {personEntries.slice(0, 20).map(e => (
                            <tr key={e.id} className={isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {e.started_at ? new Date(e.started_at).toLocaleDateString() : '—'}
                              </td>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <span className="font-mono text-xs">{e.project_number || '—'}</span>
                              </td>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                <span className="font-mono text-xs">{e.job_number || '—'}</span>
                              </td>
                              <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {activityLabel(e.activity_type)}
                              </td>
                              <td className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                                {fmt(e.hours)}h
                              </td>
                              <td className={`px-4 py-2 max-w-[200px] truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {e.description || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {personEntries.length > 20 && (
                        <div className={`px-4 py-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Showing 20 of {personEntries.length} entries
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {byPerson.length === 0 && !loading && (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No team member data available.</p>
          )}
        </div>
      )}

      {/* ======================== BY JOB ======================== */}
      {!loading && activeView === 'by_job' && (
        <div className="space-y-2">
          {byJob.map(j => {
            const isExpanded = expandedJobs.has(j.job_id);
            const jobEntries = entries.filter(e => e.job_id === j.job_id);
            return (
              <div key={j.job_id} className={`rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <button
                  onClick={() => toggleJob(j.job_id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <div>
                      <span className={`text-sm font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{j.job_number}</span>
                      <span className={`text-sm font-semibold ml-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{j.job_title}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                      j.job_status === 'completed' ? (isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700')
                      : j.job_status === 'in_progress' ? (isDark ? 'bg-blue-900/40 text-blue-400' : 'bg-blue-100 text-blue-700')
                      : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                    }`}>{j.job_status?.replace(/_/g, ' ')}</span>
                    <span className={`text-sm font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{fmt(j.total_hours)}h</span>
                  </div>
                </button>
                {isExpanded && (
                  <div className={`border-t px-4 py-3 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Billable</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>{fmt(j.billable_hours)}h</div>
                      </div>
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Travel</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{fmt(j.travel_hours)}h</div>
                      </div>
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>On-site</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{fmt(j.onsite_hours)}h</div>
                      </div>
                      <div>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Distance</span>
                        <div className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{j.travel_distance_km > 0 ? fmtKm(j.travel_distance_km) : '—'}</div>
                      </div>
                    </div>
                    {jobEntries.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className={isDark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                              <th className={`px-3 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                              <th className={`px-3 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Person</th>
                              <th className={`px-3 py-1.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Type</th>
                              <th className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hours</th>
                            </tr>
                          </thead>
                          <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                            {jobEntries.slice(0, 15).map(e => (
                              <tr key={e.id}>
                                <td className={`px-3 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {e.started_at ? new Date(e.started_at).toLocaleDateString() : '—'}
                                </td>
                                <td className={`px-3 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{e.user_name}</td>
                                <td className={`px-3 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{activityLabel(e.activity_type)}</td>
                                <td className={`px-3 py-1.5 text-right font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{fmt(e.hours)}h</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {jobEntries.length > 15 && (
                          <div className={`px-3 py-1.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Showing 15 of {jobEntries.length} entries
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {byJob.length === 0 && !loading && (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No job time data available.</p>
          )}
        </div>
      )}

      {/* ======================== ALL ENTRIES ======================== */}
      {!loading && activeView === 'entries' && (
        <div className={`rounded-lg border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'bg-gray-700/50' : 'bg-gray-50'}>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Person</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Project</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Job</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Source</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Activity</th>
                  <th className={`px-4 py-2.5 text-right font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Hours</th>
                  <th className={`px-4 py-2.5 text-center font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Billable</th>
                  <th className={`px-4 py-2.5 text-left font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Description</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>
                {entries.slice(0, 100).map(e => (
                  <tr key={e.id} className={isDark ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50'}>
                    <td className={`px-4 py-2 whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {e.started_at ? new Date(e.started_at).toLocaleDateString() : '—'}
                    </td>
                    <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{e.user_name}</td>
                    <td className={`px-4 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span className="font-mono text-xs">{e.project_number || '—'}</span>
                    </td>
                    <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      <span className="font-mono text-xs">{e.job_number || '—'}</span>
                    </td>
                    <td className={`px-4 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {e.source_item_type === 'appointment' ? (
                        <AppointmentReferenceBadge
                          appointmentId={e.source_item_id}
                          appointmentNumber={e.source_item_number}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${isDark ? 'bg-indigo-900/40 text-indigo-300 hover:bg-indigo-900/60' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'} transition-colors`}
                          iconClassName="w-3 h-3"
                          title={`Open appointment ${e.source_item_number}`}
                        >
                          <span>📋 {e.source_item_number}</span>
                        </AppointmentReferenceBadge>
                      ) : (
                        <span className="text-xs">{e.source_item_number} ({e.source_item_type})</span>
                      )}
                    </td>
                    <td className={`px-4 py-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{activityLabel(e.activity_type)}</td>
                    <td className={`px-4 py-2 text-right font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>{fmt(e.hours)}h</td>
                    <td className="px-4 py-2 text-center">
                      {e.is_billable ? (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700'}`}>Yes</span>
                      ) : (
                        <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>No</span>
                      )}
                    </td>
                    <td className={`px-4 py-2 max-w-[180px] truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {e.description || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {entries.length === 0 && (
              <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No entries found</div>
            )}
            {entries.length > 100 && (
              <div className={`px-4 py-2 text-xs border-t ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-200 text-gray-400'}`}>
                Showing 100 of {entries.length} entries
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
